import { useState, type ReactNode } from 'react';
import { api, type ExistingCommission, type RetireBlocker, type RetireCommissionResult } from '../../api/client';
import { Alert, Button, ResponsiveDialog } from '../../components/ui';
import { useOptionalAuth } from '../../hooks/useOptionalAuth';
import { hasRole } from '../../lib/jwtRoles';

// The way out of finalize's "already commissioned" 409 when the committed
// asset was deleted in the Portal. The server (billing-api
// src/commissioning/retire.ts) decides everything: it refuses while the asset
// exists or is still configured, and its dry run is what the confirm dialog
// shows. This file only keeps the 409's message and puts a button under it.

/** The existing commission when `err` is finalize's already_commissioned 409, else null. */
export function alreadyCommissioned(err: unknown): ExistingCommission | null {
  const e = err as { status?: number; body?: { code?: string; existing?: ExistingCommission } } | null;
  if (!e || e.status !== 409 || e.body?.code !== 'already_commissioned' || !e.body.existing) return null;
  return e.body.existing;
}

const TYPE_LABEL: Record<string, string> = {
  ElectricityConsumerAsset: 'Electricity',
  GroundwaterSensorAsset: 'Water',
};

type State =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'confirm'; preview: RetireCommissionResult }
  | { kind: 'retiring'; preview: RetireCommissionResult }
  | { kind: 'refused'; message: string; blockers: RetireBlocker[] }
  | { kind: 'retired' };

function refusal(err: unknown): State {
  const e = err as { message?: string; body?: { blockers?: RetireBlocker[] } };
  return { kind: 'refused', message: e.message || 'Retire failed', blockers: e.body?.blockers ?? [] };
}

export function AlreadyCommissionedNotice({
  message,
  existing,
  onCommissionAgain,
  icon,
}: {
  message: string;
  existing: ExistingCommission;
  onCommissionAgain: () => void;
  icon?: ReactNode;
}) {
  const auth = useOptionalAuth();
  const token = auth?.user?.access_token;
  const canRetire = hasRole(token, 'field:admin') || hasRole(token, 'billing:admin');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [reason, setReason] = useState('');

  async function preview() {
    setState({ kind: 'checking' });
    try {
      setState({ kind: 'confirm', preview: await api.retireCommissioning(existing.record_id, { dry_run: true }) });
    } catch (err) {
      setState(refusal(err));
    }
  }

  async function confirm(p: RetireCommissionResult) {
    setState({ kind: 'retiring', preview: p });
    try {
      await api.retireCommissioning(existing.record_id, reason.trim() ? { reason: reason.trim() } : {});
      setState({ kind: 'retired' });
    } catch (err) {
      setState(refusal(err));
    }
  }

  if (state.kind === 'retired') {
    return (
      <Alert tone="success" className="space-y-3">
        <p>Retired. {existing.meter_serial} can be commissioned again.</p>
        <Button size="sm" onClick={onCommissionAgain}>Commission again</Button>
      </Alert>
    );
  }

  const dialogState = state.kind === 'confirm' || state.kind === 'retiring' ? state : null;
  return (
    <>
      <Alert tone="danger" className="space-y-3">
        {/* min-w-0 + break-words: the message carries a 22-char asset id
            with no break opportunity, which must not widen the card at 375px. */}
        <div className="flex items-start gap-2">
          {icon}
          <span className="min-w-0 break-words">{message}</span>
        </div>
        {state.kind === 'refused' && (
          <div className="space-y-1">
            <p className="min-w-0 break-words">{state.message}</p>
            {state.blockers.length > 0 && (
              <ul className="list-disc pl-5">
                {state.blockers.map((b) => <li key={`${b.kind}:${b.id}`}>{b.label}</li>)}
              </ul>
            )}
          </div>
        )}
        {canRetire && (
          <Button size="sm" variant="secondary" onClick={preview} disabled={state.kind !== 'idle' && state.kind !== 'refused'}>
            {state.kind === 'checking' ? 'Checking…' : 'Retire old commission'}
          </Button>
        )}
      </Alert>

      {dialogState && (
        <ResponsiveDialog
          open
          onOpenChange={(open) => { if (!open && dialogState.kind === 'confirm') setState({ kind: 'idle' }); }}
          title={`Retire commission for ${existing.meter_serial}?`}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setState({ kind: 'idle' })} disabled={dialogState.kind === 'retiring'}>
                Cancel
              </Button>
              <Button size="sm" variant="danger" onClick={() => confirm(dialogState.preview)} disabled={dialogState.kind === 'retiring'}>
                {dialogState.kind === 'retiring' ? 'Retiring…' : 'Retire'}
              </Button>
            </div>
          }
        >
          <RetireEffects preview={dialogState.preview} />
          <label htmlFor="retire-reason" className="block text-sm mt-4">
            <span className="block text-content-muted mb-1">Reason (optional)</span>
          </label>
          <input
            id="retire-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. water meter commissioned as electricity"
            className="w-full min-w-0 px-3 py-2 min-h-tap lg:min-h-0 border border-border-token rounded-lg text-sm bg-surface-raised text-content"
          />
        </ResponsiveDialog>
      )}
    </>
  );
}

function RetireEffects({ preview }: { preview: RetireCommissionResult }) {
  const { record, effects } = preview;
  const readings = effects.readings_committed + effects.readings_pending;
  return (
    <ul className="space-y-2 text-sm text-content list-disc pl-5 break-words">
      <li>
        Commission {record.meter_serial} ({TYPE_LABEL[record.asset_type] ?? record.asset_type}), asset{' '}
        <span className="font-mono">{record.asset_id ?? 'none'}</span>
        {record.committed_at ? `, committed ${record.committed_at.slice(0, 10)}` : ''}: marked retired and kept, with your name and the reason.
      </li>
      {effects.meter_config_rows > 0 && (
        <li>
          Its meter config is removed
          {effects.tariff_ids.length > 0 ? ` (tariff ${effects.tariff_ids.join(', ')}; set it again on the new meter)` : ''}.
        </li>
      )}
      {readings > 0 && (
        <li>
          {effects.readings_committed} committed and {effects.readings_pending} pending reading{readings === 1 ? '' : 's'} are
          marked not applied and kept as history. The new meter starts without them: re-send the reading sheets after commissioning.
        </li>
      )}
      <li>Nothing in the Portal is changed.</li>
    </ul>
  );
}
